<template>
    <div class="popup"
         :class="{ 'has-basic-content-visible': showContent, 'is-swiping' : swipe_detected, 'swipe-to-left': swipe_direction === 'left', 'swipe-to-right': swipe_direction === 'right' }"
         :style="{ '--mgt-gallery-swipe-x': swipe_x_css_var, '--mgt-gallery-swipe-y': swipe_y_css_var, '--mgt-gallery-prev-next-scale': swipe_nav_scale}">
        <div class="overlay"></div>

        <button v-if="showNav" class="prev basic-content-small-screen" @click="()=> navigate('prev')">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                <polygon points="7.293 4.707 14.586 12 7.293 19.293 8.707 20.707 17.414 12 8.707 3.293 7.293 4.707"/>
            </svg>
        </button>
        <button v-if="showNav" class="next basic-content-small-screen" @click="()=> navigate('next')">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                <polygon points="7.293 4.707 14.586 12 7.293 19.293 8.707 20.707 17.414 12 8.707 3.293 7.293 4.707"/>
            </svg>
        </button>

        <div :class="{'inner': true,  'has-details': flipContent, 'has-gallery': media?.gallery && showGalleryThumbs}"
             :style="mediaAspectRatio"
             @click.prevent="toggleContent"
        >
            <button class="close basic-content-small-screen" @click="close">&times;</button>

            <button class="show-details basic-content-small-screen" title="Show more details."
                    @click.stop="flipContent = !flipContent">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 192 512">
                    <path
                            d="M20 424.229h20V279.771H20c-11.046 0-20-8.954-20-20V212c0-11.046 8.954-20 20-20h112c11.046 0 20 8.954 20 20v212.229h20c11.046 0 20 8.954 20 20V492c0 11.046-8.954 20-20 20H20c-11.046 0-20-8.954-20-20v-47.771c0-11.046 8.954-20 20-20zM96 0C56.235 0 24 32.235 24 72s32.235 72 72 72 72-32.235 72-72S135.764 0 96 0z"/>
                </svg>
            </button>

            <Loading v-if="loading" :thumbnail="loading.thumbnail"/>

            <div v-else class="viewer">
                <Media
                        :media="activeMedia"
                        :aspectRatio="mediaAspectRatio"
                />

                <MediaDetails
                        :media="activeMedia"
                        :title="activeMedia.title"
                        class="media-details-box"/>

                <transition name="mgt-show-content" :duration="300">
                    <div class="content" v-if="showContent">
                        <h1 class="title" v-if="activeMedia.title">{{ activeMedia.title }}</h1>

                        <div class="description"
                             v-if="activeMedia.caption || activeMedia.description"
                             v-html="activeMedia.caption || activeMedia.description"></div>
                    </div>
                </transition>
            </div>

            <GalleryThumbs
                    v-if="media?.gallery && !loading && showGalleryThumbs"
                    :gallery="media.gallery"
                    :current="currentGalleryIndex"
                    @change-gallery-item="handleGalleryItemSelection"
            />

        </div>
    </div>
</template>

<script>
import {useRoute, useRouter} from 'vue-router'
import MediaDetails from "./MediaDetails";
import Loading from "./viewer-parts/Loading";
import Media from "./viewer-parts/Media";
import GalleryThumbs from "./viewer-parts/GalleryThumbs";

export default {
    name: "MediaView",

    components: {
        MediaDetails,
        Loading,
        Media,
        GalleryThumbs
    },

    props: {
        loading: {},
        media: {},
        showNav: {
            default: true,
            type: Boolean
        },
    },

    data() {
        const SMALL_SCREEN_SIZE_IN_PX = 900;

        return {
            flipContent: false,
            currentGalleryIndex: 0,
            lastDirection: null,
            firstView: true,
            showGalleryThumbs: false,
            showContent: window.innerWidth > SMALL_SCREEN_SIZE_IN_PX,

            swipe_x_css_var: '0px',
            swipe_y_css_var: '0px',

            swipe_x: 0,
            swipe_Y: 0,
            swipe_time: new Date().getTime(),
            swipe_direction: '',
            swipe_detected: false,
            swipe_lastX: 0,
            swipe_lastY: 0,
            swipe_threshold: 20,
            swipe_nav_scale: 1
        };
    },

    setup() {
        const router = useRouter();
        const route = useRoute();
        return {router, route};
    },

    mounted() {
        document.addEventListener('keydown', this._keyNavigation);

        document.addEventListener('touchstart', this.onTouchStart, this.getTouchEvtOpts());
        document.addEventListener('touchmove', this.onTouchMove, this.getTouchEvtOpts());
        document.addEventListener('touchcancel', this.onTouchEnd, this.getTouchEvtOpts());
        document.addEventListener('touchend', this.onTouchEnd, this.getTouchEvtOpts());

        window.dispatchEvent(new Event('mgt/confetti/stop'));
    },

    beforeUnmount() {
        document.removeEventListener('keydown', this._keyNavigation);

        document.removeEventListener('touchstart', this.onTouchStart, this.getTouchEvtOpts());
        document.removeEventListener('touchmove', this.onTouchMove, this.getTouchEvtOpts());
        document.removeEventListener('touchcancel', this.onTouchEnd, this.getTouchEvtOpts());
        document.removeEventListener('touchend', this.onTouchEnd, this.getTouchEvtOpts());

        window.dispatchEvent(new Event('mgt/confetti/start'));
    },

    computed: {
        mediaAspectRatio() {
            let width = this.media?.media?.width;
            let height = this.media?.media?.height;

            if (this.media.type === 'post_gallery') {
                width = this.media?.gallery[this.currentGalleryIndex]?.width;
                height = this.media?.gallery[this.currentGalleryIndex]?.height;
            }

            return {
                aspectRatio: height && width ? `${width} / ${height} ` : '1 / 1',
                '--media-width': width ? width : 1000,
                '--media-height': height ? height : 1000,
            }
        },

        activeMedia() {
            if (this.media.type === 'post_gallery') {
                if (this.firstView && this.lastDirection === 'prev') {
                    this.currentGalleryIndex = this.media.gallery.length - 1;
                }

                if (this.firstView) {
                    this.firstView = false;
                }

                return this.media.gallery[this.currentGalleryIndex];
            }

            return this.media.media;
        },
    },

    methods: {
        toggleContent() {
            this.showContent = !this.showContent;
        },

        close() {
            this.router.push({
                name: 'mgt', query: {
                    ...this.route.query,
                    'in-view': ''
                }
            });

            this.$emit('close-popup', this.media);
        },

        handleGalleryItemSelection(item, index) {
            this.currentGalleryIndex = index;
        },

        navigateGallery(direction) {
            if (this.media.type !== 'post_gallery') {
                return false;
            }

            if (direction === 'prev') {
                if (this.currentGalleryIndex === 0) {
                    return false;
                }

                this.currentGalleryIndex--;
            }

            if (direction !== 'prev') {
                if (this.currentGalleryIndex > this.media.gallery.length - 2) {
                    // we don't need the first item of the gallery, as is already
                    return false;
                }

                this.currentGalleryIndex++;
            }


            return true;
        },

        navigate(direction) {
            this.lastDirection = direction;

            if (this.navigateGallery(direction)) {
                return;
            }

            this.firstView = true;
            this.currentGalleryIndex = 0;
            this.$emit('navigate', direction);
        },

        _keyNavigation(e) {
            switch (e.code) {
                case 'ArrowRight':
                    this.navigate('next');
                    break;

                case 'ArrowLeft':
                    this.navigate('prev');
                    break;

                case 'Space':
                case 'Enter':
                    this.flipContent = !this.flipContent;
                    break;

                case 'Escape':
                    if (this.flipContent) {
                        this.flipContent = false;
                    } else {
                        this.close()
                    }
                    break;
            }
        },

        getTouchEvtOpts() {
            return {}
        },

        onTouchStart(e) {
            const pos = this.getTouchPosition(e);
            this.swipe_x = pos.X;
            this.swipe_y = pos.Y;
            this.swipe_detected = true;
        },

        onTouchMove(e) {
            if (this.loading) {
                return;
            }

            const pos = this.getTouchPosition(e);

            const offset = Math.max(-100, Math.min(100, this.swipe_x - pos.X));
            this.swipe_x_css_var = `${-1 * offset}px`;

            this.swipe_nav_scale = Math.abs(offset) / 100;

            this.swipe_direction = this.swipe_x > pos.X ? 'left' : 'right';
        },

        onTouchEnd(e) {
            const pos = this.getTouchPosition(e);

            this.swipe_lastX = pos.X;
            this.swipe_lastY = pos.Y;
            this.swipe_detected = false;

            if (Math.abs(this.swipe_lastX - this.swipe_x) >= this.swipe_threshold) {
                if (this.swipe_lastX - this.swipe_x > this.swipe_threshold) {
                    this.navigate('prev');
                }

                if (this.swipe_lastX - this.swipe_x < this.swipe_threshold) {
                    this.navigate('next');
                }
            }

            this.swipe_x_css_var = '0px';
            this.swipe_direction = '';
        },

        getTouchPosition(e) {
            if (e.touches && e.touches[0]) {
                e = e.touches[0];
            } else {
                if (e.changedTouches && e.changedTouches[0]) {
                    e = e.changedTouches[0];
                }
            }

            return {
                X: e.clientX,
                Y: e.clientY,
            };

        }
    },
}
</script>

<style lang="scss" scoped>
@import '@/stylesheets/vars';

.popup {
    @include initVars;

    --mgt-gallery-swipe-x: 0px;
    --mgt-gallery-swipe-y: 0px;

    &.is-swiping {
        overflow: hidden;
    }
}

.overlay {
    z-index: 100010;
    position: fixed;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    background: rgba(35, 12, 70, .9);

}

.is-swiping .inner {
    transition: none;
}

.inner {
    position: fixed;
    aspect-ratio: 1 / 1;
    box-shadow: 0 0 25px rgba(0, 0, 0, .5);
    //border: 2px solid #230c46;
    //outline: 1px solid #dfba69;
    width: 90vw;
    max-width: 90vw;
    max-height: 90vh;
    top: 50%;
    transform: translate(calc(0px + var(--mgt-gallery-swipe-x)), calc(-50% + var(--mgt-gallery-swipe-y)));
    left: 0;
    right: 0;
    margin: auto;
    z-index: 100020;
    transition: all 0.3s ease-in-out;


    background-color: #fff;

    @supports not (aspect-ratio: 1 / 1) {
        height: 90vh;
    }

    @media all and (max-width: 768px) {
        width: 98vw;
        max-width: 98vw;
        &:not(.has-details).has-gallery {
            transform: translateY(calc(-50% - 100px));
        }
    }


    @media all and (min-width: 768px) {
        &.has-gallery {
            width: calc(90vw - 2 * var(--mgt-gallery-thumbs-size));
            max-width: calc(90vw - 2 * var(--mgt-gallery-thumbs-size));
            transform: translateY(-50%) translateX(calc(-0.5 * var(--mgt-gallery-thumbs-size)));
        }
    }

    &.has-details {
        aspect-ratio: unset;
        width: 900px;
        height: 100vh;
        //height: 100%;
        //aspect-ratio: 1 / 1.5 !important;
    }
}

.close {
    position: absolute;
    background: none;
    border: 0;
    right: 1rem;
    top: 1rem;
    font-size: 3rem;
    cursor: pointer;
    line-height: 1ch;
    padding: 0;
    color: #fff;
    text-shadow: 1px 1px 2px #000;
    transition: all 0.15s ease-in-out;
    z-index: 99;

    &:hover {
        color: #c00;
        transform: rotate(90deg);
        text-shadow: 1px -1px 0px #000;
    }
}

.viewer {
    height: 100%;
    position: relative;
}

.viewer {
    .has-details & {
        overflow: auto;
    }
}

.content {
    text-align: center;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    backdrop-filter: blur(10px);
    background: rgba(0, 0, 0, .7);
    padding: .5em;
    transition: all 0.5s ease-in-out;
    transition-delay: 1s;

}


.content,
.show-details {
    color: #fff;
}

.title {
    color: currentColor;
    margin-bottom: .5rem;
    padding: 0;
    line-height: 1.4;
    font-size: 1.4em;

    &:last-child {
        margin: 0;
    }
}

.description {
    font-style: italic;
}

.has-details {
    .title,
    .description {
        display: none;
    }

    .content {
        height: 3rem;
        right: 0;
        left: auto;
        //bottom: 0;
        //top: auto;
    }
}

.show-details {
    padding: 0;
    margin: 0;
    background: none;
    position: absolute;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background: rgba(0, 0, 0, .7);
    border: 1px solid rgba(255, 255, 255, .3);
    right: 4rem;
    top: 0.85rem;
    cursor: pointer;
    font-size: .5rem;
    z-index: 100;
}

.show-details:deep svg {
    fill: currentColor;
    width: 1em;
}

.swipe-to-right .prev,
.swipe-to-left .next {
    --mgt-gallery-nav-indent: 0;
    display: block !important;
    transition: none;
}

.prev,
.next {
    font-size: 3rem;
    border: 0;
    background: none;
    position: fixed;
    top: 50%;
    transform: translateY(-50%) scale(var(--mgt-gallery-prev-next-scale, 0.8));
    cursor: pointer;
    z-index: 100050;
    transition: all 0.3s ease-in-out;
    padding: 0;

    @media screen and (max-width: 1600px) {
        background: rgba(0, 0, 0, .7);
    }

    opacity: .5;

    &:hover {
        opacity: 1;
        transform: translateY(-50%) scale(1);
    }

    > svg {
        display: block;
        width: 1em;
        height: auto;
        fill: #fff;
    }
}

.prev {
    left: var(--mgt-gallery-nav-indent);
    transform-origin: left center;

    > svg {
        transform: rotate(180deg);
    }
}

.next {
    right: var(--mgt-gallery-nav-indent);
    transform-origin: right center;

    > svg {
    }
}


.mgt-show-content-leave-active,
.mgt-show-content-enter-active {
    transition: all 0.3s ease-out;
}

.mgt-show-content-enter-from,
.mgt-show-content-leave-to {
    opacity: 0;
}

@media all and (max-width: 768px) {
    .popup:not(.has-basic-content-visible) .basic-content-small-screen {
        display: none;
    }

    .prev,
    .next {
        font-size: 2rem;
    }
}

@media all and (min-width: 768px) {
    .popup:not(.has-basic-content-visible) .basic-content-large-screen {
        display: none;
    }
}
</style>